import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faFolderOpen,
  faClipboardList,
  faEnvelope,
  faDownload,
  faInfoCircle,
  faFileAlt,
  faPen,
  faTimes,
  faCopy,
  faEye,
  faEyeSlash,
  faKey,
} from '@fortawesome/free-solid-svg-icons';
import { alertService } from '../../shared/services/alertService';
import {
  useCreateMailRequestMutation,
  useBulkCreateMailRequestsMutation,
  useGetMailRequestsQuery,
  MailRequestRecord,
} from './mailRequest.service';
import { getSessionData } from 'utils.session';
import './mail-request.scss';

type TabKey = 'new-request' | 'bulk-upload' | 'track-requests';
type RoleKey = 'hr' | 'admin';

interface CredentialData {
  name: string;
  type: string;
  date: string;
  officialMail: string;
  password: string;
  mfaStatus: 'active' | 'pending';
  backupCodes: string[];
}

// Mock credentials data for the two completed rows
const MOCK_CREDENTIALS: Record<number, CredentialData> = {
  2: {
    name: 'Mohan Raj A',
    type: 'Backup Code / Reset',
    date: '11 Jul 2026',
    officialMail: 'mohanraj.a@tnschools.gov.in',
    password: 'Xk9#mP2$vL',
    mfaStatus: 'active',
    backupCodes: [
      '7KPX-M2QR',
      '3NHW-T8LZ',
      'A4VC-R9YB',
      'E6DF-S1JP',
      'Q2BK-N7GT',
      'W5RU-C4FH',
      'L9MZ-P3XK',
      'Y7AJ-D6WV',
    ],
  },
  1: {
    name: 'Salem NEET',
    type: 'New Mail',
    date: '12 Jul 2026',
    officialMail: 'salemneet.ms@tnschools.gov.in',
    password: 'Rt4@nQ7!wZ',
    mfaStatus: 'pending',
    backupCodes: [],
  },
};

const MailRequestDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('track-requests');
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  // Track locally submitted mail+type combos to prevent duplicates before cache refreshes
  const [recentSubmissions, setRecentSubmissions] = useState<string[]>([]);
  const [activeRole, setActiveRole] = useState<RoleKey>('hr');

  // Only 4028619 can see Admin/Devops view
  const loggedInUser = localStorage.getItem('username') || '';
  const isAdminUser = loggedInUser === '4028619';
  // HR users: usertype=21, usertype1=17 see only HR Team dashboard
  const session = getSessionData();
  const sessionUserType = Number(session?.usertypeid) || 0;
  const sessionUserType1 = Number(session?.emisUsertype1) || 0;
  const isHrOnlyUser = sessionUserType === 21 && sessionUserType1 === 23;

  // Force HR-only users to always stay on HR view
  React.useEffect(() => {
    if (isHrOnlyUser && activeRole !== 'hr') {
      setActiveRole('hr');
    }
  }, [isHrOnlyUser, activeRole]);

  // Force Admin user to always stay on Admin view
  React.useEffect(() => {
    if (isAdminUser && activeRole !== 'admin') {
      setActiveRole('admin');
    }
  }, [isAdminUser, activeRole]);
  const [credentialModal, setCredentialModal] = useState<CredentialData | null>(
    null
  );
  const [failedReasonModal, setFailedReasonModal] = useState<{
    name: string;
    type: string;
    date: string;
    mail: string;
    reason: string;
  } | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Process modal state
  const [processModal, setProcessModal] = useState<MailRequestRecord | null>(
    null
  );
  const [processForm, setProcessForm] = useState({
    officialMailId: '',
    newPassword: '',
    mfaStatus: 'Pending - user has not completed MFA',
    remarks: '',
  });
  const [showProcessPassword, setShowProcessPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [createMailRequest, { isLoading }] = useCreateMailRequestMutation();
  const [bulkCreateMailRequests, { isLoading: isBulkLoading }] =
    useBulkCreateMailRequestsMutation();

  // Bulk upload state
  const bulkFileRef = useRef<HTMLInputElement>(null);
  const [bulkRows, setBulkRows] = useState<any[]>([]);
  const [bulkFileName, setBulkFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Lock body scroll when any modal is open
  React.useEffect(() => {
    const isOpen =
      !!credentialModal ||
      !!processModal ||
      !!failedReasonModal ||
      showNewRequestModal ||
      showBulkUploadModal;
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [
    credentialModal,
    processModal,
    failedReasonModal,
    showNewRequestModal,
    showBulkUploadModal,
  ]);

  // Track Requests — live data
  const {
    data: trackData = [],
    isFetching: trackLoading,
    refetch: refetchTrackData,
  } = useGetMailRequestsQuery();

  // Track Requests — filter state (HR view)
  const [hrFilterType, setHrFilterType] = useState('All Types');
  const [hrFilterStatus, setHrFilterStatus] = useState('All Status');
  const [hrSearchText, setHrSearchText] = useState('');
  const [hrCurrentPage, setHrCurrentPage] = useState(1);

  // Track Requests — filter state (Admin view)
  const [adminFilterType, setAdminFilterType] = useState('All Types');
  const [adminFilterStatus, setAdminFilterStatus] = useState('All Status');
  const [adminSearchText, setAdminSearchText] = useState('');
  const [adminCurrentPage, setAdminCurrentPage] = useState(1);

  const [rowsPerPage, setRowsPerPage] = useState(25);

  // ---- Mock form state (UI-only, no logic) ----
  const [formData, setFormData] = useState({
    requestType: '1',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    secondaryMailId: '',
    preferredMailId: '',
    additionalRemarks: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name } = e.target;
    let value = e.target.value;

    // Strip leading spaces for all fields
    value = value.replace(/^\s+/, '');

    // First Name / Last Name — allow only alphabets and spaces
    if (name === 'firstName' || name === 'lastName') {
      value = value.replace(/[^a-zA-Z\s]/g, '');
    }

    // Phone Number — allow only digits, first digit must be 6-9
    if (name === 'phoneNumber') {
      const digitsOnly = value.replace(/[^0-9]/g, '');
      if (digitsOnly.length > 0 && /^[1-5]/.test(digitsOnly)) return;
      value = digitsOnly;
    }

    // Preferred Mail ID — lowercase only, must start with a letter
    // (special characters are allowed later, but not as the first character)
    if (name === 'preferredMailId') {
      value = value.toLowerCase();
      // Remove any leading non-letter characters (numbers / special chars)
      value = value.replace(/^[^a-z]+/, '');
    }

    // Secondary Mail ID — lowercase only, must start with a letter
    if (name === 'secondaryMailId') {
      value = value.toLowerCase();
      // Remove any leading non-letter characters (numbers / special chars)
      value = value.replace(/^[^a-z]+/, '');
    }

    // When request type changes, clear all other fields
    if (name === 'requestType') {
      setSubmitted(false);
      setFormData({
        requestType: value,
        firstName: '',
        lastName: '',
        phoneNumber: '',
        secondaryMailId: '',
        preferredMailId: '',
        additionalRemarks: '',
      });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleClear = () => {
    setSubmitted(false);
    setFormData((prev) => ({
      ...prev,
      firstName: '',
      lastName: '',
      phoneNumber: '',
      secondaryMailId: '',
      preferredMailId: '',
      additionalRemarks: '',
    }));
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    const { firstName, lastName, phoneNumber, preferredMailId } = formData;

    // For New Mail, all fields are required
    if (formData.requestType === '1') {
      if (
        !firstName.trim() ||
        !lastName.trim() ||
        !phoneNumber.trim() ||
        !preferredMailId.trim()
      ) {
        alertService.error('Please fill all mandatory fields');
        return;
      }

      if (
        formData.secondaryMailId &&
        /tnschools/i.test(formData.secondaryMailId)
      ) {
        alertService.error(
          'Secondary Mail ID cannot be a @tnschools.gov.in address'
        );
        return;
      }

      // Reject any whitespace or comma inside the secondary mail ID
      if (formData.secondaryMailId && /[\s,]/.test(formData.secondaryMailId)) {
        alertService.error('Invalid email format');
        return;
      }

      // Must start with a letter — special characters not allowed as first char
      if (
        formData.secondaryMailId &&
        !/^[a-zA-Z]/.test(formData.secondaryMailId.trim())
      ) {
        alertService.error('Email must start with a letter');
        return;
      }

      // Validate secondary mail ID format if provided
      // Local part may contain special characters (in the middle); leading
      // char and domain shape are still enforced.
      const emailFormatRegex = /^[a-zA-Z][^@\s]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (
        formData.secondaryMailId &&
        !emailFormatRegex.test(formData.secondaryMailId.trim())
      ) {
        alertService.error(
          'Please enter a valid email address for Secondary Mail ID'
        );
        return;
      }

      if (firstName.trim().length < 2) {
        alertService.error('First Name must be at least 2 characters');
        return;
      }

      if (phoneNumber.trim().length !== 10) {
        alertService.error(
          'Phone number must be 10 digits starting with 6, 7, 8 or 9'
        );
        return;
      }
    } else {
      // For Backup Code/Reset, Deactivate, Reactivate — only preferred mail is required
      if (!preferredMailId.trim()) {
        alertService.error('Please fill the Preferred Mail ID');
        return;
      }
    }

    // Reject any whitespace or comma inside the preferred mail ID
    if (/[\s,]/.test(preferredMailId)) {
      alertService.error('Invalid email format');
      return;
    }

    // Must start with a letter — special characters not allowed as first char
    if (!/^[a-zA-Z]/.test(preferredMailId.trim())) {
      alertService.error('Email must start with a letter');
      return;
    }

    // Validate preferred mail ID must end with @tnschools.gov.in
    // Local part may contain special characters (in the middle); only the
    // leading char (checked above) and the domain are restricted.
    const tnschoolsRegex = /^[a-zA-Z][^@\s]*@tnschools\.gov\.in$/i;
    if (!tnschoolsRegex.test(preferredMailId.trim())) {
      alertService.error('Preferred Mail ID must end with @tnschools.gov.in');
      return;
    }

    // Map numeric dropdown values to text stored in DB
    const REQUEST_TYPE_MAP: Record<string, string> = {
      '1': 'New Mail',
      '2': 'Backup Code/Reset',
      '3': 'Deactivate',
      '4': 'Reactivate',
    };
    const requestTypeText =
      REQUEST_TYPE_MAP[formData.requestType] || formData.requestType;

    try {
      const response = await createMailRequest({
        request_type: requestTypeText,
        first_name:
          formData.requestType === '1'
            ? formData.firstName
            : formData.preferredMailId.split('@')[0] || 'N/A',
        last_name: formData.requestType === '1' ? formData.lastName : 'N/A',
        phone_number:
          formData.requestType === '1' ? formData.phoneNumber : '0000000000',
        secondary_mail_id:
          formData.requestType === '1' ? formData.secondaryMailId : '',
        preferred_mail_id: formData.preferredMailId,
        additional_remarks: formData.additionalRemarks,
      }).unwrap();

      if (response?.dataStatus) {
        alertService.success(
          response?.message || 'Request submitted successfully'
        );
        handleClear();
        setShowNewRequestModal(false);
        refetchTrackData();
      } else {
        alertService.error(response?.message || 'Failed to submit request');
      }
    } catch (error: any) {
      const errMsg =
        error?.data?.message || error?.message || 'Failed to submit request';
      alertService.error(errMsg);
    }
  };

  // ---- Bulk Upload Helpers ----
  const VALID_TYPES = [
    'New Mail',
    'Backup Code/Reset',
    'Deactivate',
    'Reactivate',
  ];

  const normaliseRow = (raw: any, index: number) => {
    // Case-insensitive key lookup helper
    const get = (keys: string[]) => {
      for (const k of keys) {
        const found = Object.keys(raw).find(
          (rk) => rk.trim().toLowerCase() === k.toLowerCase()
        );
        if (
          found &&
          raw[found] !== undefined &&
          raw[found] !== null &&
          raw[found] !== ''
        )
          return String(raw[found]).trim();
      }
      return '';
    };

    const type = get(['request_type', 'Request Type', 'type']);
    const firstName = get(['first_name', 'First Name', 'firstname']);
    const lastName = get(['last_name', 'Last Name', 'lastname']);
    // Phone: convert number to string, preserve leading zeros
    const rawPhone = get(['phone_number', 'Phone', 'phone']);
    const phone = rawPhone.replace(/\D/g, ''); // strip non-digits
    const secondaryMail = get([
      'secondary_mail_id',
      'Secondary Mail',
      'secondary_mail',
    ]);
    const preferredMail = get([
      'preferred_mail_id',
      'Preferred Mail',
      'preferred_mail',
      'mail_id',
    ]);
    const remarks = get([
      'additional_remarks',
      'Remarks',
      'remarks',
      'additional_remark',
    ]);

    const issues: string[] = [];
    if (!VALID_TYPES.includes(type))
      issues.push(`Invalid request_type: "${type}"`);
    if (!preferredMail) issues.push('preferred_mail_id required');

    if (type === 'New Mail') {
      // New Mail: validate all mandatory fields + duplicate checks
      if (!firstName) issues.push('first_name required');
      if (!phone || !/^[6-9][0-9]{9}$/.test(phone))
        issues.push('Invalid phone (must be 10 digits starting with 6-9)');

      // Check duplicate mail ID against existing DB records (New Mail only)
      const isMailDuplicate =
        preferredMail &&
        trackData.some(
          (r) =>
            r.request_type === 'New Mail' &&
            r.preferred_mail_id?.toLowerCase() === preferredMail.toLowerCase()
        );
      if (isMailDuplicate) {
        issues.push('Duplicate mail ID: already exists in system');
      }

      // Check duplicate phone number against existing DB records (New Mail only)
      const isPhoneDuplicate =
        phone &&
        trackData.some(
          (r) => r.request_type === 'New Mail' && r.phone_number === phone
        );
      if (isPhoneDuplicate) {
        issues.push('Duplicate phone: already exists in system');
      }
    }

    const isDuplicate = issues.some(
      (i) => i.includes('Duplicate mail') || i.includes('Duplicate phone')
    );

    return {
      _row: index + 2,
      request_type: type,
      first_name: firstName,
      last_name: lastName,
      phone_number: phone,
      secondary_mail_id: secondaryMail,
      preferred_mail_id: preferredMail,
      additional_remarks: remarks,
      _issues: issues,
      _isDuplicate: isDuplicate,
      _status: issues.length === 0 ? 'valid' : 'error',
    };
  };

  const parseFile = (file: File) => {
    setBulkFileName(file.name);
    setBulkRows([]);
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          setBulkRows((result.data as any[]).map(normaliseRow));
        },
      });
    } else if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json: any[] = XLSX.utils.sheet_to_json(ws, { defval: '' });
        setBulkRows(json.map(normaliseRow));
      };
      reader.readAsArrayBuffer(file);
    } else {
      alertService.error('Only .csv, .xlsx or .xls files are accepted');
    }
  };

  const handleBulkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) parseFile(file);
  };

  const validRows = bulkRows.filter((r) => r._status === 'valid');
  const errorRows = bulkRows.filter((r) => r._status === 'error');

  const handleBulkSubmit = async () => {
    // Re-check duplicates at submit time against latest trackData
    const actualValidRows = validRows.filter((r) => {
      // Non-New Mail: no duplicate check needed
      if (r.request_type !== 'New Mail') return true;

      // New Mail: check duplicate mail and phone
      const isMailDuplicateNow = trackData.some(
        (existing) =>
          existing.request_type === 'New Mail' &&
          existing.preferred_mail_id?.toLowerCase() ===
            r.preferred_mail_id.toLowerCase()
      );
      const isPhoneDuplicateNow = trackData.some(
        (existing) =>
          existing.request_type === 'New Mail' &&
          existing.phone_number === r.phone_number
      );
      return !isMailDuplicateNow && !isPhoneDuplicateNow;
    });

    if (actualValidRows.length === 0) {
      alertService.error('No valid rows to submit');
      return;
    }
    try {
      const payload = actualValidRows.map((r) => {
        if (r.request_type === 'New Mail') {
          return {
            request_type: r.request_type,
            first_name: r.first_name,
            last_name: r.last_name,
            phone_number: r.phone_number,
            secondary_mail_id: r.secondary_mail_id,
            preferred_mail_id: r.preferred_mail_id,
            additional_remarks: r.additional_remarks,
          };
        }
        // Non-New Mail: only send required fields
        return {
          request_type: r.request_type,
          first_name: '',
          last_name: '',
          phone_number: '',
          secondary_mail_id: '',
          preferred_mail_id: r.preferred_mail_id,
          additional_remarks: r.additional_remarks,
        };
      });
      console.log('[BulkSubmit] payload:', payload);
      const res = await bulkCreateMailRequests(payload).unwrap();
      console.log('[BulkSubmit] response:', res);
      if (res?.dataStatus) {
        alertService.success(
          res?.message ||
            `${actualValidRows.length} records submitted successfully`
        );
        setBulkRows([]);
        setBulkFileName('');
      } else {
        alertService.error(res?.message || 'Bulk upload failed');
      }
    } catch (err: any) {
      console.error('[BulkSubmit] error:', err);
      alertService.error(
        err?.data?.message ||
          err?.message ||
          'Bulk upload failed. Check console for details.'
      );
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'request_type',
      'first_name',
      'last_name',
      'phone_number',
      'secondary_mail_id',
      'preferred_mail_id',
      'additional_remarks',
    ];
    const sample = [
      [
        'New Mail',
        'Ravi',
        'Kumar',
        '9876543210',
        'ravi@gmail.com',
        'ravi.kumar@tnschools.gov.in',
        '',
      ],
      [
        'Backup Code/Reset',
        '',
        '',
        '',
        '',
        'priya.k@tnschools.gov.in',
        'Reset needed',
      ],
      ['Deactivate', '', '', '', '', 'rajan.m@tnschools.gov.in', 'Resigned'],
      ['Reactivate', '', '', '', '', 'salem.neet@tnschools.gov.in', 'Rejoined'],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sample]);

    // Force phone_number column (D, index 3) to text to preserve leading zeros
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let R = 1; R <= range.e.r; R++) {
      const cellAddr = XLSX.utils.encode_cell({ r: R, c: 3 });
      if (ws[cellAddr]) {
        ws[cellAddr].t = 's';
        ws[cellAddr].z = '@';
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MailRequests');
    XLSX.writeFile(wb, 'mail_request_template.xlsx');
  };

  const typeBadgeClass = (type: string) => {
    switch (type) {
      case 'New Mail':
        return 'type-new-mail';
      case 'Backup Code/Reset':
        return 'type-backup';
      case 'Deactivate':
        return 'type-deactivate';
      case 'Reactivate':
        return 'type-reactivate';
      default:
        return 'type-new-mail';
    }
  };

  const handleExportCsv = () => {
    const currentFilteredData =
      activeRole === 'hr' ? hrFilteredData : adminFilteredData;
    if (currentFilteredData.length === 0) {
      alertService.error('No records to export');
      return;
    }
    const headers = [
      '#',
      'Request Type',
      'First Name',
      'Last Name',
      'Phone',
      'Secondary Mail',
      'Preferred Mail ID',
      'Date',
      'Status',
      'Remarks',
      'Password',
      'Backup Code',
      'MFA Status',
    ];
    const rows = currentFilteredData.map((row, idx) => [
      idx + 1,
      row.request_type,
      row.first_name,
      row.last_name,
      row.phone_number,
      row.secondary_mail_id || '',
      row.preferred_mail_id || '',
      row.created_at,
      row.status === 1 ? 'Completed' : row.status === 2 ? 'Failed' : 'Pending',
      row.additional_remarks || '',
      row.status === 1 ? row.password || '' : '',
      row.status === 1 ? row.backup_code || '' : '',
      row.mfastatus === 1 ? 'MFA Active' : 'Pending',
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'mail_requests');
    XLSX.writeFile(wb, 'mail_requests.csv');
  };

  // HR view filtered data — Pending first, then Completed
  const hrFilteredData = trackData
    .filter(
      (r) => hrFilterType === 'All Types' || r.request_type === hrFilterType
    )
    .filter((r) => {
      if (hrFilterStatus === 'All Status') return true;
      if (hrFilterStatus === 'Completed') return r.status === 1;
      if (hrFilterStatus === 'Pending') return !r.status || r.status === 0;
      if (hrFilterStatus === 'Failed') return r.status === 2;
      return true;
    })
    .filter((r) => {
      if (!hrSearchText.trim()) return true;
      const q = hrSearchText.toLowerCase();
      const name = `${r.first_name} ${r.last_name}`.toLowerCase();
      return (
        name.includes(q) ||
        (r.preferred_mail_id || '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const order: Record<number, number> = { 2: 0, 0: 1, 1: 2 };
      return (order[a.status] ?? 1) - (order[b.status] ?? 1);
    });

  // Admin view filtered data — Pending first, then Completed
  const adminFilteredData = trackData
    .filter(
      (r) =>
        adminFilterType === 'All Types' || r.request_type === adminFilterType
    )
    .filter((r) => {
      if (adminFilterStatus === 'All Status') return true;
      if (adminFilterStatus === 'Completed') return r.status === 1;
      if (adminFilterStatus === 'Pending') return !r.status || r.status === 0;
      if (adminFilterStatus === 'Failed') return r.status === 2;
      return true;
    })
    .filter((r) => {
      if (!adminSearchText.trim()) return true;
      const q = adminSearchText.toLowerCase();
      const name = `${r.first_name} ${r.last_name}`.toLowerCase();
      return (
        name.includes(q) ||
        (r.preferred_mail_id || '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const order: Record<number, number> = { 2: 0, 0: 1, 1: 2 };
      return (order[a.status] ?? 1) - (order[b.status] ?? 1);
    });

  // Pagination — HR
  const hrTotalPages = Math.ceil(hrFilteredData.length / rowsPerPage);
  const hrPaginatedData = hrFilteredData.slice(
    (hrCurrentPage - 1) * rowsPerPage,
    hrCurrentPage * rowsPerPage
  );

  // Pagination — Admin
  const adminTotalPages = Math.ceil(adminFilteredData.length / rowsPerPage);
  const adminPaginatedData = adminFilteredData.slice(
    (adminCurrentPage - 1) * rowsPerPage,
    adminCurrentPage * rowsPerPage
  );

  // Reset to page 1 when HR filters change
  React.useEffect(() => {
    setHrCurrentPage(1);
  }, [hrFilterType, hrFilterStatus, hrSearchText, rowsPerPage]);

  // Reset to page 1 when Admin filters change
  React.useEffect(() => {
    setAdminCurrentPage(1);
  }, [adminFilterType, adminFilterStatus, adminSearchText, rowsPerPage]);

  return (
    <div className="mail-request-container">
      {/* ---- Page Title ---- */}
      <div className="mail-request-title">
        <h4>
          {activeRole === 'hr'
            ? 'Mail Request Portal'
            : 'Mail Request Admin Queue'}
        </h4>
      </div>

      {/* ================= HR TEAM VIEW ================= */}
      {activeRole === 'hr' && (
        <>
          {/* ---- Tab Bar ---- */}
          <div className="mail-request-tabs">
            <div
              className={`tab-item ${activeTab === 'track-requests' ? 'active' : ''}`}
              onClick={() => setActiveTab('track-requests')}
            >
              <FontAwesomeIcon icon={faClipboardList} className="tab-icon" />
              <span>Track Requests</span>
              <span className="tab-badge">{trackData.length}</span>
            </div>

            <div className="tab-right-actions">
              <div
                className="tab-item"
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    requestType: '1',
                    firstName: '',
                    lastName: '',
                    phoneNumber: '',
                    secondaryMailId: '',
                    preferredMailId: '',
                    additionalRemarks: '',
                  });
                  setShowNewRequestModal(true);
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="tab-icon" />
                <span>New Request</span>
              </div>
              <div
                className="tab-item"
                onClick={() => {
                  setBulkRows([]);
                  setBulkFileName('');
                  setShowBulkUploadModal(true);
                }}
              >
                <FontAwesomeIcon icon={faFolderOpen} className="tab-icon" />
                <span>Bulk Upload</span>
              </div>
            </div>
          </div>

          {/* ---- New Request Modal ---- */}
          {showNewRequestModal && (
            <div
              className="cred-modal-overlay"
              onClick={() => setShowNewRequestModal(false)}
            >
              <div
                className="cred-modal new-request-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="cred-modal-header">
                  <h5 className="cred-modal-title">Raise Mail Request</h5>
                  <button
                    className="cred-modal-close"
                    onClick={() => setShowNewRequestModal(false)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                <div className="mail-form-grid">
                  {/* Request Type */}
                  <div className="mail-form-group">
                    <label>
                      Request Type <span className="required">*</span>
                    </label>
                    <select
                      name="requestType"
                      value={formData.requestType}
                      onChange={handleChange}
                    >
                      <option value="1">New Mail</option>
                      <option value="2">Backup Code/Reset</option>
                      <option value="3">Deactivate</option>
                      <option value="4">Reactivate</option>
                    </select>
                  </div>

                  {/* First Name - only for New Mail */}
                  {formData.requestType === '1' && (
                    <div className="mail-form-group">
                      <label>
                        First Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        placeholder=""
                        value={formData.firstName}
                        onChange={handleChange}
                        maxLength={50}
                        className={
                          submitted && !formData.firstName.trim()
                            ? 'input-error'
                            : ''
                        }
                      />
                      {submitted && !formData.firstName.trim() && (
                        <span className="field-error">
                          First Name is required (min 2 characters)
                        </span>
                      )}
                    </div>
                  )}

                  {/* Last Name - only for New Mail */}
                  {formData.requestType === '1' && (
                    <div className="mail-form-group">
                      <label>
                        Last Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        placeholder=""
                        value={formData.lastName}
                        onChange={handleChange}
                        maxLength={50}
                        className={
                          submitted && !formData.lastName.trim()
                            ? 'input-error'
                            : ''
                        }
                      />
                      {submitted && !formData.lastName.trim() && (
                        <span className="field-error">
                          Last Name is required
                        </span>
                      )}
                    </div>
                  )}

                  {/* Phone Number - only for New Mail */}
                  {formData.requestType === '1' && (
                    <div className="mail-form-group">
                      <label>
                        Phone Number <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="phoneNumber"
                        placeholder="10-digit mobile"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        maxLength={10}
                        className={
                          submitted && !formData.phoneNumber.trim()
                            ? 'input-error'
                            : ''
                        }
                      />
                      <span className="field-hint">
                        Used for MFA / verification
                      </span>
                      {submitted && !formData.phoneNumber.trim() && (
                        <span className="field-error">
                          Phone number is required
                        </span>
                      )}
                    </div>
                  )}

                  {/* Secondary Mail ID - only for New Mail */}
                  {formData.requestType === '1' && (
                    <div className="mail-form-group">
                      <label>Secondary Mail ID</label>
                      <input
                        type="text"
                        name="secondaryMailId"
                        placeholder="personal@gmail.com"
                        value={formData.secondaryMailId}
                        onChange={handleChange}
                        className={
                          formData.secondaryMailId &&
                          /tnschools/i.test(formData.secondaryMailId)
                            ? 'input-error'
                            : ''
                        }
                      />
                      <span className="field-hint">
                        Personal recovery email
                      </span>
                      {formData.secondaryMailId &&
                        /tnschools/i.test(formData.secondaryMailId) && (
                          <span className="field-error">
                            Secondary Mail ID cannot be a @tnschools.gov.in
                            address
                          </span>
                        )}
                    </div>
                  )}

                  {/* Preferred Mail ID */}
                  <div className="mail-form-group">
                    <label>
                      Preferred Mail ID <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="preferredMailId"
                      placeholder="name.x@tnschools.gov.in"
                      value={formData.preferredMailId}
                      onChange={handleChange}
                      className={
                        submitted &&
                        (!formData.preferredMailId.trim() ||
                          !/^[a-zA-Z0-9._%+-]+@tnschools\.gov\.in$/i.test(
                            formData.preferredMailId.trim()
                          ))
                          ? 'input-error'
                          : ''
                      }
                    />
                    <span className="field-hint">
                      Must end with @tnschools.gov.in
                    </span>
                    {submitted && !formData.preferredMailId.trim() && (
                      <span className="field-error">
                        Preferred Mail ID is required
                      </span>
                    )}
                    {submitted &&
                      formData.preferredMailId.trim() &&
                      !/^[a-zA-Z0-9._%+-]+@tnschools\.gov\.in$/i.test(
                        formData.preferredMailId.trim()
                      ) && (
                        <span className="field-error">
                          Must end with @tnschools.gov.in
                        </span>
                      )}
                  </div>
                </div>

                {/* Additional Remarks */}
                <div className="remarks-section">
                  <label>Add Remarks</label>
                  <input
                    type="text"
                    name="additionalRemarks"
                    placeholder="Any extra context for the admin team"
                    value={formData.additionalRemarks}
                    onChange={handleChange}
                  />
                </div>

                {/* Action Buttons */}
                <div className="mail-form-actions">
                  <button
                    className="btn-submit"
                    onClick={handleSubmit}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Submitting...' : 'Submit Request'}
                  </button>
                  <button
                    className="btn-clear"
                    onClick={handleClear}
                    disabled={isLoading}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ---- Bulk Upload Modal ---- */}
          {showBulkUploadModal && (
            <div
              className="cred-modal-overlay"
              onClick={() => setShowBulkUploadModal(false)}
            >
              <div
                className="cred-modal bulk-upload-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="cred-modal-header">
                  <h5 className="cred-modal-title">Bulk Upload Requests</h5>
                  <button
                    className="cred-modal-close"
                    onClick={() => setShowBulkUploadModal(false)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                {/* Info Banner */}
                <div className="bulk-info-banner">
                  <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
                  <span>
                    Upload a CSV or Excel file with multiple requests at once.{' '}
                    <span
                      className="template-link"
                      onClick={handleDownloadTemplate}
                      style={{ cursor: 'pointer' }}
                    >
                      Download the template
                    </span>{' '}
                    to see required columns per request type. Rows with errors
                    are highlighted — you can fix and re-upload, or submit valid
                    rows only.
                  </span>
                </div>

                {/* Download Template */}
                <div className="bulk-template-row">
                  <button
                    className="btn-download-template"
                    onClick={handleDownloadTemplate}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    &nbsp; Download Template CSV
                  </button>
                  <span className="template-hint">
                    All 4 request types included as example rows
                  </span>
                </div>

                {/* Drag & Drop Zone */}
                <input
                  type="file"
                  ref={bulkFileRef}
                  accept=".csv,.xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleBulkFileChange}
                />
                <div
                  className={`bulk-dropzone ${isDragging ? 'dragging' : ''}`}
                  onClick={() => bulkFileRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  <FontAwesomeIcon icon={faFileAlt} className="dropzone-icon" />
                  <p className="dropzone-text">
                    <strong>
                      {bulkFileName
                        ? bulkFileName
                        : 'Click to choose CSV or drag & drop here'}
                    </strong>
                  </p>
                  <p className="dropzone-hint">
                    Accepts .csv, .xlsx files · Max 500 rows per upload
                  </p>
                </div>

                {/* Preview Table */}
                {bulkRows.length > 0 && (
                  <>
                    {/* Summary Badges */}
                    <div className="bulk-summary-badges">
                      <span className="bulk-badge badge-valid">
                        ✓ Valid: {validRows.length}
                      </span>
                      {errorRows.length > 0 && (
                        <span className="bulk-badge badge-error">
                          ⚠ Errors: {errorRows.length}
                        </span>
                      )}
                      <span className="bulk-badge badge-total">
                        Total: {bulkRows.length}
                      </span>
                    </div>

                    <div
                      className="track-table-wrapper"
                      style={{ marginTop: '1rem' }}
                    >
                      <table className="track-table">
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Type</th>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>Mail ID</th>
                            <th>Status</th>
                            <th>Issues</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkRows.map((row, idx) => (
                            <tr
                              key={idx}
                              className={
                                row._isDuplicate
                                  ? 'bulk-row-duplicate'
                                  : row._status === 'error'
                                    ? 'bulk-row-error'
                                    : 'bulk-row-valid'
                              }
                            >
                              <td>{row._row}</td>
                              <td>
                                {row.request_type ? (
                                  <span
                                    className={`type-badge ${typeBadgeClass(row.request_type)}`}
                                  >
                                    {row.request_type}
                                  </span>
                                ) : (
                                  <span className="type-badge type-deactivate">
                                    —
                                  </span>
                                )}
                              </td>
                              <td>
                                {row.request_type === 'New Mail'
                                  ? `${row.first_name} ${row.last_name}`.trim() ||
                                    '—'
                                  : '—'}
                              </td>
                              <td>
                                {row.request_type === 'New Mail'
                                  ? row.phone_number || '—'
                                  : '—'}
                              </td>
                              <td className="mail-cell">
                                {row.preferred_mail_id || '—'}
                              </td>
                              <td>
                                {row._status === 'valid' ? (
                                  <span className="bulk-status-valid">
                                    ✓ Valid
                                  </span>
                                ) : (
                                  <span className="bulk-status-error">
                                    ⚠ Error
                                  </span>
                                )}
                              </td>
                              <td
                                style={{
                                  fontSize: '0.75rem',
                                  color: '#dc2626',
                                }}
                              >
                                {row._issues.join('; ') || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Submit */}
                    <div
                      className="mail-form-actions"
                      style={{ marginTop: '1rem' }}
                    >
                      <button
                        className="btn-submit"
                        onClick={handleBulkSubmit}
                        disabled={isBulkLoading || validRows.length === 0}
                      >
                        {isBulkLoading
                          ? 'Submitting...'
                          : `Submit ${validRows.length} Valid Row${validRows.length !== 1 ? 's' : ''}`}
                      </button>
                      <button
                        className="btn-clear"
                        onClick={() => {
                          setBulkRows([]);
                          setBulkFileName('');
                        }}
                        disabled={isBulkLoading}
                      >
                        Clear
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ---- Track Requests Tab ---- */}
          {activeTab === 'track-requests' && (
            <div className="mail-request-card">
              {/* Filters Row */}
              <div className="track-filters-row">
                <select
                  className="track-filter-select"
                  value={hrFilterType}
                  onChange={(e) => setHrFilterType(e.target.value)}
                >
                  <option value="All Types">All Types</option>
                  <option value="New Mail">New Mail</option>
                  <option value="Backup Code/Reset">Backup Code/Reset</option>
                  <option value="Deactivate">Deactivate</option>
                  <option value="Reactivate">Reactivate</option>
                </select>

                <select
                  className="track-filter-select"
                  value={hrFilterStatus}
                  onChange={(e) => setHrFilterStatus(e.target.value)}
                >
                  <option value="All Status">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                  <option value="Failed">Failed</option>
                </select>

                <input
                  type="text"
                  className="track-search-input"
                  placeholder="Search name or mail id..."
                  value={hrSearchText}
                  onChange={(e) =>
                    setHrSearchText(e.target.value.replace(/^\s+/, ''))
                  }
                />

                <button className="btn-export-csv" onClick={handleExportCsv}>
                  <FontAwesomeIcon icon={faDownload} />
                  &nbsp; Export CSV
                </button>
              </div>

              {/* Table */}
              <div className="track-table-wrapper">
                {trackLoading ? (
                  <p
                    style={{
                      padding: '1rem',
                      color: '#6b7280',
                      fontSize: '0.85rem',
                    }}
                  >
                    Loading...
                  </p>
                ) : hrFilteredData.length === 0 ? (
                  <p
                    style={{
                      padding: '1rem',
                      color: '#6b7280',
                      fontSize: '0.85rem',
                    }}
                  >
                    No records found.
                  </p>
                ) : (
                  <table className="track-table">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Type</th>
                        <th>Name</th>
                        <th>Official Mail ID</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Remarks</th>
                        <th>Credentials</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hrPaginatedData.map(
                        (row: MailRequestRecord, idx: number) => (
                          <tr key={row.id}>
                            <td>
                              {(hrCurrentPage - 1) * rowsPerPage + idx + 1}
                            </td>
                            <td>
                              <span
                                className={`type-badge ${typeBadgeClass(row.request_type)}`}
                              >
                                {row.request_type}
                              </span>
                            </td>
                            <td>
                              {`${row.first_name} ${row.last_name}`.trim()}
                            </td>
                            <td className="mail-cell">
                              {row.preferred_mail_id || '—'}
                            </td>
                            <td>{row.created_at}</td>
                            <td>
                              {row.status === 1 ? (
                                <>
                                  <span className="status-dot status-completed"></span>{' '}
                                  Completed
                                </>
                              ) : row.status === 2 ? (
                                <>
                                  <span className="status-dot status-failed"></span>{' '}
                                  Failed
                                </>
                              ) : (
                                <>
                                  <span className="status-dot status-pending"></span>{' '}
                                  Pending
                                </>
                              )}
                            </td>
                            <td>{row.additional_remarks || '—'}</td>
                            <td>
                              {row.status === 1 ? (
                                <button
                                  className="btn-view-creds"
                                  onClick={() => {
                                    setCredentialModal({
                                      name: `${row.first_name} ${row.last_name}`.trim(),
                                      type: row.request_type,
                                      date: row.created_at,
                                      officialMail: row.preferred_mail_id || '',
                                      password: row.password || '',
                                      mfaStatus:
                                        row.mfastatus === 1
                                          ? 'active'
                                          : 'pending',
                                      backupCodes: row.backup_code
                                        ? row.backup_code
                                            .split(',')
                                            .map((c: string) => c.trim())
                                        : [],
                                    });
                                    setShowPassword(false);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                  &nbsp; View
                                </button>
                              ) : row.status === 2 ? (
                                <button
                                  className="btn-view-failed"
                                  onClick={() => {
                                    setFailedReasonModal({
                                      name: `${row.first_name} ${row.last_name}`.trim(),
                                      type: row.request_type,
                                      date: row.created_at,
                                      mail: row.preferred_mail_id || '',
                                      reason:
                                        row.request_status ||
                                        'No reason provided',
                                    });
                                  }}
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                  &nbsp; View
                                </button>
                              ) : (
                                <span
                                  style={{
                                    color: '#9ca3af',
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  —
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              <div className="pagination-controls">
                <span className="pagination-info">
                  Showing{' '}
                  {hrFilteredData.length === 0
                    ? 0
                    : (hrCurrentPage - 1) * rowsPerPage + 1}{' '}
                  to{' '}
                  {Math.min(hrCurrentPage * rowsPerPage, hrFilteredData.length)}{' '}
                  of {hrFilteredData.length} entries
                </span>
                <button
                  disabled={hrCurrentPage === 1}
                  onClick={() => setHrCurrentPage(1)}
                >
                  &laquo;
                </button>
                <button
                  disabled={hrCurrentPage === 1}
                  onClick={() => setHrCurrentPage((p) => p - 1)}
                >
                  &lsaquo;
                </button>
                <button
                  disabled={
                    hrCurrentPage === hrTotalPages || hrTotalPages === 0
                  }
                  onClick={() => setHrCurrentPage((p) => p + 1)}
                >
                  &rsaquo;
                </button>
                <button
                  disabled={
                    hrCurrentPage === hrTotalPages || hrTotalPages === 0
                  }
                  onClick={() => setHrCurrentPage(hrTotalPages)}
                >
                  &raquo;
                </button>
                <select
                  className="pagination-size-select"
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          )}
          {/* ================= END HR TEAM VIEW ================= */}
        </>
      )}

      {/* ================= ADMIN / DEVOPS VIEW ================= */}
      {activeRole === 'admin' && (
        <>
          {/* Summary Cards — derived from live data */}
          <div className="admin-summary-cards">
            <div className="summary-card">
              <span className="summary-value summary-pending">
                {trackData.filter((r) => !r.status || r.status === 0).length}
              </span>
              <span className="summary-label">PENDING</span>
            </div>
            <div className="summary-card">
              <span className="summary-value summary-completed">
                {trackData.filter((r) => r.status === 1).length}
              </span>
              <span className="summary-label">COMPLETED</span>
            </div>
            <div className="summary-card">
              <span className="summary-value summary-failed">
                {trackData.filter((r) => r.status === 2).length}
              </span>
              <span className="summary-label">FAILED</span>
            </div>
            <div className="summary-card">
              <span className="summary-value summary-total">
                {trackData.length}
              </span>
              <span className="summary-label">TOTAL REQUESTS</span>
            </div>
          </div>

          {/* Filters Row */}
          <div className="track-filters-row">
            <select
              className="track-filter-select"
              value={adminFilterType}
              onChange={(e) => setAdminFilterType(e.target.value)}
            >
              <option value="All Types">All Types</option>
              <option value="New Mail">New Mail</option>
              <option value="Backup Code/Reset">Backup Code/Reset</option>
              <option value="Deactivate">Deactivate</option>
              <option value="Reactivate">Reactivate</option>
            </select>

            <select
              className="track-filter-select"
              value={adminFilterStatus}
              onChange={(e) => setAdminFilterStatus(e.target.value)}
            >
              <option value="All Status">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Completed">Completed</option>
              <option value="Failed">Failed</option>
            </select>

            <input
              type="text"
              className="track-search-input"
              placeholder="Search name or mail id..."
              value={adminSearchText}
              onChange={(e) =>
                setAdminSearchText(e.target.value.replace(/^\s+/, ''))
              }
            />

            <button className="btn-export-csv" onClick={handleExportCsv}>
              <FontAwesomeIcon icon={faDownload} />
              &nbsp; Export CSV
            </button>
          </div>

          {/* Admin Table */}
          <div className="track-table-wrapper">
            {trackLoading ? (
              <p
                style={{
                  padding: '1rem',
                  color: '#6b7280',
                  fontSize: '0.85rem',
                }}
              >
                Loading...
              </p>
            ) : adminFilteredData.length === 0 ? (
              <p
                style={{
                  padding: '1rem',
                  color: '#6b7280',
                  fontSize: '0.85rem',
                }}
              >
                No records found.
              </p>
            ) : (
              <table className="track-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Type</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Secondary Mail</th>
                    <th>Official Mail ID</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {adminPaginatedData.map(
                    (row: MailRequestRecord, idx: number) => (
                      <tr key={row.id}>
                        <td>
                          {(adminCurrentPage - 1) * rowsPerPage + idx + 1}
                        </td>
                        <td>
                          <span
                            className={`type-badge ${typeBadgeClass(row.request_type)}`}
                          >
                            {row.request_type}
                          </span>
                        </td>
                        <td>{`${row.first_name} ${row.last_name}`.trim()}</td>
                        <td>{row.phone_number || '—'}</td>
                        <td>{row.secondary_mail_id || '—'}</td>
                        <td className="mail-cell">
                          {row.preferred_mail_id || '—'}
                        </td>
                        <td>{row.created_at}</td>
                        <td>
                          {row.status === 1 ? (
                            <>
                              <span className="status-dot status-completed"></span>{' '}
                              Completed
                            </>
                          ) : row.status === 2 ? (
                            <>
                              <span className="status-dot status-failed"></span>{' '}
                              Failed
                            </>
                          ) : (
                            <>
                              <span className="status-dot status-pending"></span>{' '}
                              Pending
                            </>
                          )}
                        </td>
                        <td>
                          <button
                            className="btn-action-process"
                            onClick={() => {
                              if (row.status === 2) {
                                setFailedReasonModal({
                                  name: `${row.first_name} ${row.last_name}`.trim(),
                                  type: row.request_type,
                                  date: row.created_at,
                                  mail: row.preferred_mail_id || '',
                                  reason:
                                    row.request_status || 'No reason provided',
                                });
                              } else {
                                setProcessForm({
                                  officialMailId: row.preferred_mail_id || '',
                                  newPassword: '',
                                  mfaStatus:
                                    'Pending - user has not completed MFA',
                                  remarks: '',
                                });
                                setShowProcessPassword(false);
                                setProcessModal(row);
                              }
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          <div className="pagination-controls">
            <span className="pagination-info">
              Showing{' '}
              {adminFilteredData.length === 0
                ? 0
                : (adminCurrentPage - 1) * rowsPerPage + 1}{' '}
              to{' '}
              {Math.min(
                adminCurrentPage * rowsPerPage,
                adminFilteredData.length
              )}{' '}
              of {adminFilteredData.length} entries
            </span>
            <button
              disabled={adminCurrentPage === 1}
              onClick={() => setAdminCurrentPage(1)}
            >
              &laquo;
            </button>
            <button
              disabled={adminCurrentPage === 1}
              onClick={() => setAdminCurrentPage((p) => p - 1)}
            >
              &lsaquo;
            </button>
            <button
              disabled={
                adminCurrentPage === adminTotalPages || adminTotalPages === 0
              }
              onClick={() => setAdminCurrentPage((p) => p + 1)}
            >
              &rsaquo;
            </button>
            <button
              disabled={
                adminCurrentPage === adminTotalPages || adminTotalPages === 0
              }
              onClick={() => setAdminCurrentPage(adminTotalPages)}
            >
              &raquo;
            </button>
            <select
              className="pagination-size-select"
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </>
      )}
      {/* Failed Reason Modal */}
      {failedReasonModal && (
        <div
          className="cred-modal-overlay"
          onClick={() => setFailedReasonModal(null)}
        >
          <div
            className="cred-modal"
            style={{ maxWidth: '480px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cred-modal-header">
              <div>
                <h5 className="cred-modal-title">
                  Failed Request —{' '}
                  {failedReasonModal.name || failedReasonModal.mail}
                </h5>
                <span className="cred-modal-subtitle">
                  {failedReasonModal.type} · {failedReasonModal.date}
                </span>
              </div>
              <button
                className="cred-modal-close"
                onClick={() => setFailedReasonModal(null)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <div style={{ marginBottom: '0.75rem' }}>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: '#6b7280',
                  }}
                >
                  OFFICIAL MAIL
                </span>
                <div
                  style={{
                    fontSize: '0.95rem',
                    color: '#111827',
                    marginTop: '0.25rem',
                  }}
                >
                  {failedReasonModal.mail}
                </div>
              </div>
              <div
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '1rem',
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: '#dc2626',
                    display: 'block',
                    marginBottom: '0.5rem',
                  }}
                >
                  FAILURE REASON
                </span>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#991b1b' }}>
                  {failedReasonModal.reason}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {credentialModal && (
        <div
          className="cred-modal-overlay"
          onClick={() => setCredentialModal(null)}
        >
          <div className="cred-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="cred-modal-header">
              <div>
                <h5 className="cred-modal-title">
                  Credentials — {credentialModal.name}
                </h5>
                <span className="cred-modal-subtitle">
                  {credentialModal.type} · {credentialModal.date}
                </span>
              </div>
              <button
                className="cred-modal-close"
                onClick={() => setCredentialModal(null)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Fields */}
            <div className="cred-field-row">
              <span className="cred-field-label">OFFICIAL MAIL</span>
              <span className="cred-field-value cred-mono">
                {credentialModal.officialMail}
              </span>
              <button
                className="cred-copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(credentialModal.officialMail);
                  alertService.success('Official Mail copied');
                }}
              >
                <FontAwesomeIcon icon={faCopy} />
              </button>
            </div>

            <div className="cred-field-row">
              <span className="cred-field-label">NEW PASSWORD</span>
              <span className="cred-field-value cred-mono">
                {showPassword ? credentialModal.password : '••••••••••'}
              </span>
              <button
                className="cred-show-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
              <button
                className="cred-copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(credentialModal.password);
                  alertService.success('Password copied');
                }}
              >
                <FontAwesomeIcon icon={faCopy} />
              </button>
            </div>

            <div className="cred-field-row">
              <span className="cred-field-label">MFA STATUS</span>
              <span className="cred-field-value">
                {credentialModal.mfaStatus === 'active' ? (
                  <span className="mfa-badge mfa-active">
                    <span className="status-dot status-completed"></span> MFA
                    Active
                  </span>
                ) : (
                  <span className="mfa-badge mfa-pending">
                    <span className="status-dot status-pending"></span> MFA
                    Pending — user must complete setup
                  </span>
                )}
              </span>
            </div>

            {/* MFA Backup Codes */}
            <div className="cred-backup-section">
              <div className="cred-backup-header">
                <span className="cred-backup-title">
                  <FontAwesomeIcon icon={faKey} className="key-icon" />
                  &nbsp; MFA Backup Codes
                </span>
                {credentialModal.backupCodes.length > 0 && (
                  <button
                    className="btn-copy-all"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        credentialModal.backupCodes.join(', ')
                      );
                      alertService.success('All backup codes copied');
                    }}
                  >
                    <FontAwesomeIcon icon={faCopy} />
                    &nbsp; Copy All
                  </button>
                )}
              </div>
              <p className="cred-backup-hint">
                Each code works once. Keep them confidential.
              </p>

              {credentialModal.backupCodes.length > 0 ? (
                <div className="cred-backup-grid">
                  {credentialModal.backupCodes.map((code, idx) => (
                    <div className="cred-backup-code" key={idx}>
                      <span className="code-text">{code}</span>
                      <button
                        className="cred-copy-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(code);
                          alertService.success(`Code "${code}" copied`);
                        }}
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="cred-backup-empty">
                  No backup codes yet. Admin will add them after MFA completion.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---- Process Request Modal ---- */}
      {processModal &&
        (() => {
          const isNewMail = processModal.request_type === 'New Mail';
          const isBackup = processModal.request_type === 'Backup Code/Reset';
          const isReactivate = processModal.request_type === 'Reactivate';
          const isDeactivate = processModal.request_type === 'Deactivate';
          const showCurrentMail = isReactivate || isDeactivate || isBackup;
          const showNewPassword = isNewMail || isBackup;
          const rowNumber =
            adminFilteredData.length -
            adminFilteredData.findIndex((r) => r.id === processModal.id);

          return (
            <div
              className="cred-modal-overlay"
              onClick={() => setProcessModal(null)}
            >
              <div
                className="cred-modal process-modal"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="cred-modal-header">
                  <div>
                    <h5 className="cred-modal-title">
                      Process Request #{rowNumber} —{' '}
                      {`${processModal.first_name} ${processModal.last_name}`.trim()}
                    </h5>
                    <span className="cred-modal-subtitle">
                      {processModal.request_type} - Submitted{' '}
                      {processModal.created_at}
                    </span>
                  </div>
                  <button
                    className="cred-modal-close"
                    onClick={() => setProcessModal(null)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                {/* Summary Box */}
                <div className="process-summary-row">
                  <div className="process-summary-item">
                    <span className="process-summary-label">PHONE</span>
                    <span className="process-summary-value">
                      {processModal.phone_number || '—'}
                    </span>
                  </div>
                  <div className="process-summary-item">
                    <span className="process-summary-label">
                      SECONDARY MAIL
                    </span>
                    <span className="process-summary-value">
                      {processModal.secondary_mail_id || '—'}
                    </span>
                  </div>
                  <div className="process-summary-item">
                    <span className="process-summary-label">TYPE</span>
                    <span
                      className={`type-badge ${typeBadgeClass(processModal.request_type)}`}
                    >
                      {processModal.request_type}
                    </span>
                  </div>

                  {/* Current Mail ID — shown for Reactivate, Deactivate, Backup */}
                  {showCurrentMail && (
                    <div className="process-summary-item process-summary-full">
                      <span className="process-summary-label">
                        CURRENT MAIL ID
                      </span>
                      <span className="process-summary-value cred-mono">
                        {processModal.preferred_mail_id || '—'}
                      </span>
                    </div>
                  )}

                  {/* Description/Reason — shown for Reactivate and Deactivate */}
                  {(isReactivate || isDeactivate) && (
                    <div className="process-summary-item process-summary-full">
                      <span className="process-summary-label">
                        DESCRIPTION / REASON
                      </span>
                      <span className="process-summary-value">
                        {processModal.additional_remarks || '—'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Credentials Section — same layout for both completed and pending */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <div className="process-section-title">
                    🔑 Saved Credentials
                  </div>
                  <div className="cred-field-row">
                    <span className="cred-field-label">PASSWORD</span>
                    <span className="cred-field-value cred-mono">
                      {processModal.status === 1
                        ? showProcessPassword
                          ? processModal.password || '—'
                          : '••••••••••'
                        : '—'}
                    </span>
                    {processModal.status === 1 && (
                      <>
                        <button
                          className="cred-show-btn"
                          onClick={() =>
                            setShowProcessPassword(!showProcessPassword)
                          }
                        >
                          {showProcessPassword ? 'Hide' : 'Show'}
                        </button>
                        <button
                          className="cred-copy-btn"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              processModal.password || ''
                            );
                            alertService.success('Password copied');
                          }}
                        >
                          <FontAwesomeIcon icon={faCopy} />
                        </button>
                      </>
                    )}
                  </div>
                  <div
                    className="cred-field-row"
                    style={{ borderBottom: 'none' }}
                  >
                    <span className="cred-field-label">BACKUP CODES</span>
                    <span className="cred-field-value cred-mono">
                      {processModal.status === 1 && processModal.backup_code
                        ? processModal.backup_code
                        : '—'}
                    </span>
                    {processModal.status === 1 && processModal.backup_code && (
                      <button
                        className="cred-copy-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            processModal.backup_code || ''
                          );
                          alertService.success('Backup codes copied');
                        }}
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Enter Credentials */}
                <div className="process-section-title">
                  <FontAwesomeIcon icon={faPen} style={{ color: '#6b7280' }} />
                  &nbsp; Enter Credentials
                  <span className="process-section-hint">
                    (created in Google Admin Console)
                  </span>
                </div>

                <div className="process-field-group">
                  <label>OFFICIAL MAIL ID</label>
                  <input
                    type="text"
                    value={processForm.officialMailId}
                    onChange={(e) =>
                      setProcessForm({
                        ...processForm,
                        officialMailId: e.target.value.replace(/^\s+/, ''),
                      })
                    }
                    placeholder="official.mail@tnschools.gov.in"
                    readOnly={processModal.status === 1}
                  />
                </div>

                {/* MFA & Backup Codes */}
                <div
                  className="process-section-title"
                  style={{ marginTop: '1.25rem' }}
                >
                  🔐 MFA &amp; Backup Codes
                </div>

                <div
                  className="process-field-group"
                  style={{ marginTop: '0.75rem' }}
                >
                  <label>MFA STATUS</label>
                  {processModal.mfastatus === 1 ? (
                    <div className="process-status-auto">
                      <span className="status-dot status-completed"></span>
                      <span
                        style={{
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          color: '#22c55e',
                        }}
                      >
                        MFA Active — Completed
                      </span>
                    </div>
                  ) : (
                    <div className="process-status-auto">
                      <span className="status-dot status-pending"></span>
                      <span className="process-status-text">
                        Pending - user has not completed MFA
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="process-modal-actions">
                  {/* <button className="btn-save-notify" onClick={() => {}}>
                    ✓ Save &amp; Notify HR
                  </button> */}
                  <button
                    className="btn-cancel-process"
                    onClick={() => setProcessModal(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default MailRequestDashboard;
